import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Center,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconChevronRight, IconPlus, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { NewSalesOrderModal } from './NewSalesOrderModal';
import { SalesOrderStatusBadge } from './SalesOrderStatusBadge';
import { listSalesOrders, type SalesOrderDto } from './api';

type Scope = 'open' | 'all';

/**
 * Sales orders — what is owed to customers.
 *
 * Opens on OPEN rather than all, which is legacy's `OrderStatus in (1,2,5)`: the question somebody
 * comes to this screen with is "what do we still owe", not "what have we ever sold". The All tab is
 * there for looking something up afterwards.
 *
 * No warehouse filter: the list is already scoped to the warehouse the session is working in, so a
 * filter would offer a choice the server has already made.
 */
export function SalesOrdersRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [scope, setScope] = useState<Scope>('open');
  const [search, setSearch] = useState('');

  const canWrite = permissions.has('orders:write');

  const query = useQuery({
    queryKey: ['sales-orders', scope] as const,
    queryFn: ({ signal }) => listSalesOrders({ openOnly: scope === 'open' }, signal),
  });

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (o) =>
        o.orderNumber.toLowerCase().includes(needle) ||
        o.customerName.toLowerCase().includes(needle) ||
        o.customerNumber.toLowerCase().includes(needle) ||
        (o.customerPo?.toLowerCase().includes(needle) ?? false),
    );
  }, [query.data, search]);

  function openOrder(id: string) {
    void navigate({ to: '/app/sales-orders/$id', params: { id } });
  }

  const columns: DataColumn<SalesOrderDto>[] = useMemo(
    () => [
      {
        key: 'number',
        label: t('salesOrders.number'),
        card: 'title',
        // The number is the link, as it is on the purchase order list: it is what people
        // read first and what they mean when they say "open 1042".
        render: (o) => (
          <Anchor
            fw={500}
            onClick={() =>
              void navigate({ to: '/app/sales-orders/$id', params: { id: o.salesOrderId } })
            }
          >
            <Code>{o.orderNumber}</Code>
          </Anchor>
        ),
      },
      {
        key: 'customer',
        label: t('salesOrders.customer'),
        card: 'subtitle',
        render: (o) => o.customerName,
      },
      {
        key: 'customerPo',
        label: t('salesOrders.customerPo'),
        // What the customer quotes when they ring up, so it earns a column rather than living
        // only on the detail screen.
        render: (o) => <Text size="sm">{o.customerPo ?? '—'}</Text>,
      },
      {
        key: 'ordered',
        label: t('salesOrders.ordered'),
        render: (o) => <Text size="sm">{new Date(o.orderDateUtc).toLocaleDateString()}</Text>,
      },
      {
        key: 'requested',
        label: t('salesOrders.requested'),
        render: (o) => (
          <Text size="sm">
            {o.requestedShipDateUtc
              ? new Date(o.requestedShipDateUtc).toLocaleDateString()
              : '—'}
          </Text>
        ),
      },
      {
        key: 'lines',
        label: t('salesOrders.lines'),
        render: (o) => <Text size="sm">{o.lineCount}</Text>,
      },
      {
        key: 'total',
        label: t('salesOrders.total'),
        render: (o) => (
          <Text size="sm">
            {o.totalAmount.toLocaleString(undefined, {
              style: 'currency',
              currency: o.currencyCode,
            })}
          </Text>
        ),
      },
      {
        key: 'status',
        label: t('salesOrders.status'),
        card: 'aside',
        render: (o) => <SalesOrderStatusBadge status={o.status} />,
      },
    ],
    [t, navigate],
  );

  return (
    <>
      <PageHeader
        title={t('nav.salesOrders')}
        actions={
          canWrite ? (
            <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={open}>
              {t('salesOrders.new')}
            </Button>
          ) : null
        }
      />

      <Stack gap="md">
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            label={t('salesOrders.search')}
            placeholder={t('salesOrders.searchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', sm: 300 }}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('salesOrders.show')}
            </Text>
            <SegmentedControl
              value={scope}
              onChange={(v) => setScope(v as Scope)}
              data={[
                { label: t('salesOrders.openOnly'), value: 'open' },
                { label: t('salesOrders.all'), value: 'all' },
              ]}
            />
          </Stack>
        </Group>

        {query.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {query.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('salesOrders.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(query.error as Error).message}</Text>
              {query.error instanceof ApiError && query.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                  {t('salesOrders.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {search.trim()
              ? t('salesOrders.noMatch')
              : scope === 'open'
                ? t('salesOrders.noneOpen')
                : t('salesOrders.empty')}
          </Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(o) => o.salesOrderId}
            minWidth={900}
            actionsWidth={60}
            striped
            highlightOnHover
            actions={(o) => (
              <ActionIcon
                variant="subtle"
                color="invetrak"
                aria-label={t('salesOrders.openOrder', { number: o.orderNumber })}
                onClick={() => openOrder(o.salesOrderId)}
              >
                <IconChevronRight size={16} />
              </ActionIcon>
            )}
          />
        )}
      </Stack>

      <NewSalesOrderModal
        opened={modalOpen}
        onClose={close}
        onCreated={(id) => {
          void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
          // Straight to the order: it was created with no lines, and an order with no lines is
          // not something anybody wanted — they wanted to start typing one.
          openOrder(id);
        }}
      />
    </>
  );
}
